import React, { useRef } from 'react';
import { Add as AddIcon } from '@mui/icons-material';
import Button from '../../../common/inputs/Button';
import Section from '../../../layout/Section';
import LoanTable from '../../../tables/LoanTable';
import { EditableTableRef } from '../../../tables/common/EditableTable';
import { LoanWithPlan, SelectOption, ValidationsResponse } from '../../../../types';

interface LoanSectionProps {
    /** Liste aller Darlehen mit Tilgungsplänen */
    loans: LoanWithPlan[];
    /** Optionen für den Darlehenstyp (vom Backend geladen) */
    darlehensTypOptions: SelectOption[];
    /** Kann der Benutzer bearbeiten? */
    canEdit: boolean;
    /** Projektlaufzeit in Jahren (wird an LoanTable/LoanAuszahlungDetail weitergereicht) */
    projektLaufzeit: number;
    /** Benötigtes Fremdkapital (für Vorbelegung neuer Darlehen) */
    fremdkapital?: number;
    /** Summe aller bestehenden Darlehen (für Vorbelegung neuer Darlehen) */
    summeDarlehen?: number;
    /** Callback wenn ein Darlehen gelöscht wurde */
    onLoanDeleted?: (loanId: string) => void;
    /** Callback wenn Daten geändert wurden (Create/Update/Delete) */
    onDataChanged?: () => void;
    /** Callback für Validierungen aus API-Responses → PageWarningsContext */
    onValidationsReceived?: (validations: ValidationsResponse) => void;
}

/**
 * Darlehenssektion - zeigt alle Darlehen in einer editierbaren Tabelle
 * Ermöglicht das Hinzufügen, Bearbeiten und Löschen von Darlehen direkt in der Tabelle
 */
const LoanSection: React.FC<LoanSectionProps> = ({
    loans,
    darlehensTypOptions,
    canEdit,
    projektLaufzeit,
    fremdkapital = 0,
    summeDarlehen = 0,
    onLoanDeleted,
    onDataChanged,
    onValidationsReceived,
}) => {
    // Ref zur LoanTable für Zugriff auf startCreating
    const tableRef = useRef<EditableTableRef>(null);

    /**
     * Handler für den Button "Darlehen anlegen"
     * Startet den Create-Modus mit dem offenen Fremdkapitalbedarf als Vorbelegung
     */
    const handleNewLoan = () => {
        // Offener Bedarf = Fremdkapital - Summe bestehender Darlehen (mindestens 0)
        const offenerBedarf = Math.max(0, fremdkapital - summeDarlehen);
        tableRef.current?.startCreating({ darlehenshoehe: offenerBedarf });
    };

    const safeLoans = Array.isArray(loans) ? loans : [];

    return (
        <Section
            title="Darlehen"
            noPadding={true}
            actions={
                canEdit ? (
                    <Button
                        variant="primary"
                        startIcon={<AddIcon />}
                        onClick={handleNewLoan}
                    >
                        Darlehen anlegen
                    </Button>
                ) : undefined
            }
        >
            <LoanTable
                ref={tableRef}
                loans={safeLoans}
                darlehensTypOptions={darlehensTypOptions}
                canEdit={canEdit}
                projektLaufzeit={projektLaufzeit}
                onLoanDeleted={onLoanDeleted}
                onDataChanged={onDataChanged}
                onValidationsReceived={onValidationsReceived}
            />
        </Section>
    );
};

export default LoanSection;
